/**
 * 飛行・対空システム ヘルパー関数
 *
 * UI版（magic-spirit.tsx）とシミュレータ（GameActions.ts）で共有する
 * 戦闘判定ロジックを提供する。
 *
 * @see src/ルール/future/flight_system_v1.md - 仕様書
 * @see src/ルール/engine/flight-system-implementation.md - 実装設計書
 */

import {
  hasHikou,
  hasTaiku,
  isGroundMonster as isGroundMonsterBase,
  hasAirDefender as hasAirDefenderBase,
  hasGroundMonster as hasGroundMonsterBase,
} from './index';
import type { FieldArray, FieldMonster } from './index';
import { STATUS_EFFECT_TYPES } from '../statusEffects/statusTypes';

// =============================================================================
// 型定義
// =============================================================================

/** ダイレクトアタックステータス */
export interface DirectAttackStatus {
  /** ダイレクトアタック可能か */
  canDirect: boolean;
  /** 理由（UI表示用） */
  reason: string;
  /** ダメージ倍率（0.5 = 50%軽減） */
  damageMultiplier: number;
  /** 反撃ダメージ倍率（0 = なし, 0.3 = 30%, 1.0 = 100%） */
  counterMultiplier: number;
}

/** フィールドカード情報（簡略型） */
interface FieldCardInfo {
  id: string;
  [key: string]: unknown;
}

// =============================================================================
// 彷徨い状態による飛行効果
// =============================================================================

/**
 * モンスターが彷徨い状態かチェック
 * @param monster - モンスター
 * @returns 彷徨い状態かどうか
 */
export function hasWanderingStatus(monster: FieldMonster | null): boolean {
  if (!monster?.statusEffects) return false;
  return (monster.statusEffects as Array<{ type: string }>).some(
    s => s.type === STATUS_EFFECT_TYPES.WANDERING
  );
}

/**
 * モンスターが飛行効果を持つか判定（キーワード + 彷徨い状態）
 * @param monster - モンスター
 * @returns 飛行効果を持つかどうか
 */
export function hasFlightEffect(monster: FieldMonster | null): boolean {
  if (!monster) return false;
  // キーワードによる飛行
  if (hasHikou(monster)) return true;
  // 彷徨い状態による飛行
  if (hasWanderingStatus(monster)) return true;
  return false;
}

/**
 * 地上モンスターか判定（飛行キーワードも彷徨い状態も持たない）
 * @param monster - モンスター
 * @returns 地上モンスターかどうか
 */
export function isGroundMonster(monster: FieldMonster | null): boolean {
  if (!monster) return false;
  return !hasFlightEffect(monster);
}

/**
 * フィールド上に飛行または対空を持つモンスターがいるか
 * （飛行モンスターのダイレクトアタックを迎撃できるモンスターがいるか）
 * @param field - フィールド配列
 * @returns 飛行/対空モンスターがいるかどうか
 */
export function hasAirDefender(field: FieldArray): boolean {
  return field.some(monster =>
    monster && (hasFlightEffect(monster) || hasTaiku(monster))
  );
}

/**
 * フィールド上に地上モンスターがいるか
 * @param field - フィールド配列
 * @returns 地上モンスターがいるかどうか
 */
export function hasGroundMonster(field: FieldArray): boolean {
  return field.some(monster => monster && isGroundMonster(monster));
}

// =============================================================================
// フィールドカードからのキーワード付与
// =============================================================================

/** 【対空】を付与するフィールドカードIDのリスト */
const TAIKU_GRANTING_FIELD_CARDS = ['C0000551']; // 対空陣地

/**
 * フィールドカードから【対空】が付与されているか判定
 * @param ownerFieldCard - オーナーのフィールドカード
 * @returns 対空が付与されるかどうか
 */
export function hasTaikuFromFieldCard(ownerFieldCard: FieldCardInfo | null | undefined): boolean {
  if (!ownerFieldCard) return false;
  return TAIKU_GRANTING_FIELD_CARDS.includes(ownerFieldCard.id);
}

/**
 * 【対空】を持つか判定（CSV + フィールドカード付与）
 * @param card - カードオブジェクト
 * @param ownerFieldCard - オーナーのフィールドカード（オプション）
 * @returns 対空を持つかどうか
 */
export function hasTaikuWithFieldCard(
  card: FieldMonster | null,
  ownerFieldCard?: FieldCardInfo | null
): boolean {
  if (!card) return false;
  // CSV/カード自体のキーワードをチェック
  if (hasTaiku(card)) return true;
  // フィールドカードからの付与をチェック
  if (hasTaikuFromFieldCard(ownerFieldCard)) return true;
  return false;
}

/**
 * フィールド上に飛行または対空を持つモンスターがいるか（フィールドカード付与考慮）
 * @param field - フィールド配列
 * @param fieldCard - フィールドカード（オプション）
 * @returns 飛行/対空モンスターがいるかどうか
 */
export function hasAirDefenderWithFieldCard(
  field: FieldArray,
  fieldCard?: FieldCardInfo | null
): boolean {
  // フィールドカードが対空付与なら、モンスターがいれば迎撃可能
  if (hasTaikuFromFieldCard(fieldCard)) {
    return field.some(monster => monster !== null);
  }
  // 通常のチェック
  return hasAirDefender(field);
}

// =============================================================================
// 攻撃対象選択
// =============================================================================

/**
 * 攻撃可能なターゲットを取得
 *
 * 地上モンスターは飛行モンスターを攻撃できない。
 * 対空モンスターは飛行モンスターを攻撃できる。
 * 飛行モンスター（彷徨い含む）は全てのモンスターを攻撃できる。
 *
 * @param attacker - 攻撃モンスター
 * @param opponentField - 相手フィールド
 * @returns 攻撃可能なスロットインデックスの配列
 */
export function getValidAttackTargets(
  attacker: FieldMonster,
  opponentField: FieldArray
): number[] {
  const validTargets: number[] = [];

  opponentField.forEach((target, index) => {
    if (!target) return;

    // 地上モンスターは飛行を攻撃できない（対空を持っていれば可能）
    // 彷徨い状態のモンスターも飛行扱い
    if (isGroundMonster(attacker) && !hasTaiku(attacker) && hasFlightEffect(target)) {
      return; // 攻撃不可
    }

    validTargets.push(index);
  });

  return validTargets;
}

// =============================================================================
// ダイレクトアタック判定
// =============================================================================

/**
 * ダイレクトアタック可否と条件を判定
 *
 * 【飛行モンスターの場合（彷徨い含む）】
 * - 相手に飛行/対空がいる → ダイレクト不可（迎撃される）
 * - 相手に地上のみ → ダイレクト可能（50%軽減、反撃なし）
 *
 * 【地上モンスターの場合】
 * - 相手に地上モンスターがいる → ダイレクト不可
 * - 相手に飛行のみ → ダイレクト可能（50%軽減、飛行から100%反撃）
 * - 相手にモンスターなし → ダイレクト可能（軽減なし）
 *
 * @param attacker - 攻撃モンスター
 * @param opponentField - 相手フィールド
 * @returns ダイレクトアタックステータス
 */
export function getDirectAttackStatus(
  attacker: FieldMonster,
  opponentField: FieldArray
): DirectAttackStatus {
  const opponentMonsters = opponentField.filter(m => m !== null);

  // 相手モンスターがいない場合
  if (opponentMonsters.length === 0) {
    return {
      canDirect: true,
      reason: '相手モンスターなし',
      damageMultiplier: 1.0,  // 軽減なし
      counterMultiplier: 0,    // 反撃なし
    };
  }

  // 攻撃側が飛行の場合（彷徨い状態も含む）
  if (hasFlightEffect(attacker)) {
    // 相手に飛行または対空がいる場合 → 迎撃される
    if (hasAirDefender(opponentField)) {
      return {
        canDirect: false,
        reason: '相手に飛行/対空がいるため迎撃される',
        damageMultiplier: 0,
        counterMultiplier: 0,
      };
    }

    // 相手に地上のみ → ダイレクト可能（50%軽減、反撃なし）
    const reason = hasWanderingStatus(attacker)
      ? '【彷徨い】地上を無視してダイレクト'
      : '【飛行】地上を無視してダイレクト';
    return {
      canDirect: true,
      reason,
      damageMultiplier: 0.5,
      counterMultiplier: 0,  // 反撃なし
    };
  }

  // 攻撃側が地上の場合
  // 相手に地上モンスターがいる → ダイレクト不可
  if (hasGroundMonster(opponentField)) {
    return {
      canDirect: false,
      reason: '相手に地上モンスターがいる',
      damageMultiplier: 0,
      counterMultiplier: 0,
    };
  }

  // 相手に飛行のみ → ダイレクト可能（50%軽減、飛行から100%反撃）
  return {
    canDirect: true,
    reason: '相手に飛行のみ - ダイレクト可能だが反撃あり',
    damageMultiplier: 0.5,
    counterMultiplier: 1.0,  // 飛行から100%反撃
  };
}

// =============================================================================
// 反撃ダメージ計算
// =============================================================================

/**
 * 反撃ダメージ倍率を計算
 *
 * - 飛行（彷徨い含む） → 地上: 反撃なし（0）
 * - それ以外: 通常の30%反撃（0.3）
 *
 * @param attacker - 攻撃モンスター
 * @param defender - 防御モンスター
 * @returns 反撃ダメージ倍率（0, 0.3, 1.0）
 */
export function getCounterAttackMultiplier(
  attacker: FieldMonster,
  defender: FieldMonster | null
): number {
  // ダイレクトアタック時（defenderがnull）は別途処理
  if (!defender) {
    return 0;
  }

  // 飛行（彷徨い含む） → 地上: 反撃なし
  if (hasFlightEffect(attacker) && isGroundMonster(defender)) {
    return 0;
  }

  // それ以外: 通常の30%反撃
  return 0.3;
}

// =============================================================================
// ダイレクトアタック時の反撃ダメージ計算
// =============================================================================

/**
 * 地上モンスターがダイレクトアタックした時の飛行モンスターからの反撃ダメージを計算
 *
 * 彷徨い状態のモンスターも飛行扱いで反撃可能（ただし攻撃力は0なのでダメージはない）
 *
 * @param opponentField - 相手フィールド
 * @param counterMultiplier - 反撃倍率（通常1.0 = 100%）
 * @returns 総反撃ダメージ
 */
export function calculateFlightCounterDamage(
  opponentField: FieldArray,
  counterMultiplier: number
): number {
  if (counterMultiplier === 0) return 0;

  // 飛行効果を持つモンスター（彷徨い含む）からの反撃
  const flyingMonsters = opponentField.filter(m => m && hasFlightEffect(m));
  const totalCounterDamage = flyingMonsters.reduce((sum, m) => {
    // ?? を使用: currentAttack が 0 の場合も正しく 0 を使う（|| だと 0 が falsy で attack を参照してしまう）
    return sum + Math.floor((m?.currentAttack ?? m?.attack ?? 0) * counterMultiplier);
  }, 0);

  return totalCounterDamage;
}

// =============================================================================
// AI用ヘルパー
// =============================================================================

/**
 * 飛行モンスターにとって最適な攻撃対象を選択
 *
 * 飛行モンスター（彷徨い含む）の場合、地上を優先（反撃なし）
 *
 * @param attacker - 攻撃モンスター
 * @param opponentField - 相手フィールド
 * @param validTargets - 攻撃可能なターゲットのインデックス配列
 * @returns 最適なターゲットインデックス
 */
export function selectBestTargetForFlight(
  attacker: FieldMonster,
  opponentField: FieldArray,
  validTargets: number[]
): number {
  if (validTargets.length === 0) return -1;

  // 飛行モンスター（彷徨い含む）の場合、地上を優先（反撃なし）
  if (hasFlightEffect(attacker)) {
    const groundTargets = validTargets.filter(idx => {
      const target = opponentField[idx];
      return target && isGroundMonster(target);
    });

    if (groundTargets.length > 0) {
      // 地上の中でHP最小を選択
      return groundTargets.reduce((best, idx) => {
        const target = opponentField[idx];
        const bestTarget = opponentField[best];
        return (target?.currentHp || target?.hp || 0) < (bestTarget?.currentHp || bestTarget?.hp || 0)
          ? idx
          : best;
      }, groundTargets[0]);
    }
  }

  // 通常選択（HP最小）
  return validTargets.reduce((best, idx) => {
    const target = opponentField[idx];
    const bestTarget = opponentField[best];
    return (target?.currentHp || target?.hp || 0) < (bestTarget?.currentHp || bestTarget?.hp || 0)
      ? idx
      : best;
  }, validTargets[0]);
}

// =============================================================================
// フィールドカード対応版（拡張）
// =============================================================================

/**
 * 攻撃可能なターゲットを取得（フィールドカード対応）
 *
 * @param attacker - 攻撃モンスター
 * @param opponentField - 相手フィールド
 * @param attackerFieldCard - 攻撃側のフィールドカード（オプション）
 * @returns 攻撃可能なスロットインデックスの配列
 */
export function getValidAttackTargetsWithFieldCard(
  attacker: FieldMonster,
  opponentField: FieldArray,
  attackerFieldCard?: FieldCardInfo | null
): number[] {
  const validTargets: number[] = [];

  opponentField.forEach((target, index) => {
    if (!target) return;

    // 地上モンスターは飛行を攻撃できない（対空を持っていれば可能）
    // フィールドカードからの対空付与も考慮
    // 彷徨い状態のモンスターも飛行扱い
    const hasTaikuEffect = hasTaikuWithFieldCard(attacker, attackerFieldCard);
    if (isGroundMonster(attacker) && !hasTaikuEffect && hasFlightEffect(target)) {
      return; // 攻撃不可
    }

    validTargets.push(index);
  });

  return validTargets;
}

/**
 * ダイレクトアタック可否と条件を判定（フィールドカード対応）
 *
 * @param attacker - 攻撃モンスター
 * @param opponentField - 相手フィールド
 * @param opponentFieldCard - 相手のフィールドカード（オプション）
 * @returns ダイレクトアタックステータス
 */
export function getDirectAttackStatusWithFieldCard(
  attacker: FieldMonster,
  opponentField: FieldArray,
  opponentFieldCard?: FieldCardInfo | null
): DirectAttackStatus {
  const opponentMonsters = opponentField.filter(m => m !== null);

  // 相手モンスターがいない場合
  if (opponentMonsters.length === 0) {
    return {
      canDirect: true,
      reason: '相手モンスターなし',
      damageMultiplier: 1.0,
      counterMultiplier: 0,
    };
  }

  // 攻撃側が飛行の場合（彷徨い状態も含む）
  if (hasFlightEffect(attacker)) {
    // 相手に飛行または対空がいる場合 → 迎撃される
    // フィールドカードからの対空付与も考慮
    if (hasAirDefenderWithFieldCard(opponentField, opponentFieldCard)) {
      return {
        canDirect: false,
        reason: '相手に飛行/対空がいるため迎撃される',
        damageMultiplier: 0,
        counterMultiplier: 0,
      };
    }

    // 相手に地上のみ → ダイレクト可能（50%軽減、反撃なし）
    const reason = hasWanderingStatus(attacker)
      ? '【彷徨い】地上を無視してダイレクト'
      : '【飛行】地上を無視してダイレクト';
    return {
      canDirect: true,
      reason,
      damageMultiplier: 0.5,
      counterMultiplier: 0,
    };
  }

  // 攻撃側が地上の場合
  // 相手に地上モンスターがいる → ダイレクト不可
  if (hasGroundMonster(opponentField)) {
    return {
      canDirect: false,
      reason: '相手に地上モンスターがいる',
      damageMultiplier: 0,
      counterMultiplier: 0,
    };
  }

  // 相手に飛行のみ → ダイレクト可能（50%軽減、飛行から100%反撃）
  return {
    canDirect: true,
    reason: '相手に飛行のみ - ダイレクト可能だが反撃あり',
    damageMultiplier: 0.5,
    counterMultiplier: 1.0,
  };
}
